/**
 * Shared utilities for parsing AI guidance responses
 */

export interface AbielGuidance {
  mode: 'guide';
  summary: string;
  reading_goals: string[];
  how_to_pray: {
    outline: {
      address: string;
      praise: string;
      confession?: string;
      request: string;
      submission: string;
      thanksgiving: string;
    };
    bullet_points: string[];
  };
  prompts: {
    en: string[];
    zh?: string[];
  };
  journaling_questions: string[];
  cross_references: string[];
  notes?: string;
}

export function stripCodeFences(s: string): string {
  if (!s) return s;
  return s.replace(/```[\s\S]*?```/g, '').trim();
}

export function extractJsonFromSummary(s: string): any | null {
  const m = s?.match(/```json\s*([\s\S]*?)```/i);
  if (!m) return null;
  try {
    return JSON.parse(m[1]);
  } catch {
    return null;
  }
}

/**
 * Parse guidance response robustly, handling stringified JSON,
 * embedded JSON, or direct objects.
 */
export function parseGuidanceResponse(response: any): AbielGuidance {
  console.log('[parseGuidanceResponse] Raw response:', response);

  const safeParseGuidanceString = (s: string): any | null => {
    if (!s) return null;
    try {
      let str = stripCodeFences(s).trim();

      if (
        (str.startsWith("'") && str.endsWith("'")) ||
        (str.startsWith('"') && str.endsWith('"'))
      ) {
        str = str.slice(1, -1).trim();
      }

      const firstBrace = str.indexOf('{');
      const lastBrace = str.lastIndexOf('}');
      if (firstBrace !== -1 && lastBrace !== -1) {
        const candidate = str.slice(firstBrace, lastBrace + 1);
        try {
          return JSON.parse(candidate);
        } catch (e) {
          console.warn('[parseGuidanceResponse] Candidate parse failed:', e);
        }
      }

      return JSON.parse(str);
    } catch (e) {
      console.warn('[parseGuidanceResponse] safeParseGuidanceString failed:', e);
      return null;
    }
  };

  try {
    let rawGuidance: any = null;

    if (response?.guidance) {
      rawGuidance =
        typeof response.guidance === 'string'
          ? safeParseGuidanceString(response.guidance)
          : response.guidance;
    } else if (typeof response === 'string') {
      rawGuidance = safeParseGuidanceString(response);
    } else if (typeof response === 'object') {
      rawGuidance = response;
    }

    if (rawGuidance && isValidGuidance(rawGuidance)) {
      return normalizeGuidance(rawGuidance);
    }

    throw new Error('Invalid guidance format');
  } catch (error) {
    console.warn('[parseGuidanceResponse] Parse failed:', error);

    return {
      mode: 'guide',
      summary:
        typeof response === 'string'
          ? stripCodeFences(response)
          : 'I apologize, but I encountered an issue generating your prayer guidance. Please try again.',
      reading_goals: ['Ask God to guide your prayer time'],
      how_to_pray: {
        outline: {
          address: 'Begin by addressing God as your loving Father',
          praise: 'Thank God for His faithfulness and love',
          request: 'Share your needs and concerns with God',
          submission: "Submit your will to God's perfect plan",
          thanksgiving: 'Thank God for His grace and mercy',
        },
        bullet_points: [
          'Find a quiet place for prayer',
          'Begin with worship and gratitude',
          'Be honest about your needs',
          'Listen for God’s guidance',
          'End with thanksgiving',
        ],
      },
      prompts: { en: ['How is God calling you to pray?', 'What is on your heart today?'] },
      journaling_questions: [
        'What is God speaking to your heart?',
        'How can you trust God more in this situation?',
      ],
      cross_references: [],
      notes: 'Please try your request again if needed.',
    };
  }
}

function isValidGuidance(obj: any): obj is AbielGuidance {
  return (
    obj &&
    typeof obj === 'object' &&
    typeof obj.summary === 'string' &&
    Array.isArray(obj.reading_goals) &&
    obj.how_to_pray &&
    obj.prompts
  );
}

function ensureSummary(guidance: AbielGuidance): AbielGuidance {
  if (!guidance.summary || guidance.summary.trim().length === 0) {
    if (guidance.reading_goals?.length) {
      guidance.summary = `Prayer guidance focusing on: ${guidance.reading_goals[0]}`;
    } else if (guidance.how_to_pray?.outline?.address) {
      guidance.summary = `Prayer structure beginning with: ${guidance.how_to_pray.outline.address}`;
    } else if (guidance.journaling_questions?.length) {
      guidance.summary = `Reflection starting with: ${guidance.journaling_questions[0]}`;
    } else {
      guidance.summary = 'Prayer guidance and spiritual reflection';
    }
  }
  return guidance;
}

function normalizeGuidance(obj: any): AbielGuidance {
  const g: AbielGuidance = {
    mode: 'guide',
    summary: stripCodeFences(obj.summary || ''),
    reading_goals: obj.reading_goals ?? [],
    how_to_pray: obj.how_to_pray ?? {
      outline: {
        address: '',
        praise: '',
        request: '',
        submission: '',
        thanksgiving: '',
      },
      bullet_points: [],
    },
    prompts: obj.prompts?.en
      ? obj.prompts
      : { en: obj.prompts || [] },
    journaling_questions: obj.journaling_questions ?? [],
    cross_references:
      obj.cross_references ??
      obj.crossReferences ??
      obj.refs ??
      [],
    notes: obj.notes,
  };

  const enriched = enrichGuidanceFromSummary(g);
  return ensureSummary(enriched);
}

function enrichGuidanceFromSummary(guidance: AbielGuidance): AbielGuidance {
  const embedded = extractJsonFromSummary(guidance.summary);
  if (embedded) {
    guidance.reading_goals ||= embedded.reading_goals;
    guidance.how_to_pray ||= embedded.how_to_pray;
    guidance.prompts ||= embedded.prompts;
    guidance.journaling_questions ||= embedded.journaling_questions;
    guidance.cross_references ||= embedded.cross_references;
    guidance.notes ||= embedded.notes;
  }

  guidance.summary = stripCodeFences(guidance.summary || '');
  return guidance;
}
